import { createAuthenticatedGraphQLClient } from '../../auth/createAuthenticatedGraphQLClient';
import { resolveClientEnv } from '../../graphql/config/resolveClientEnv';
import type { RuntimeEnvironment } from '../../graphql/config/runtimeConfig';
import type {
  CancelSeedDataUploadMutation,
  CancelSeedDataUploadMutationVariables,
} from '../../graphql/queries/cancelSeedDataUpload.generated';
import type {
  ConfirmSeedDataUploadMutation,
  ConfirmSeedDataUploadMutationVariables,
} from '../../graphql/queries/confirmSeedDataUpload.generated';
import type {
  CreateSeedDataUploadMutation,
  CreateSeedDataUploadMutationVariables,
} from '../../graphql/queries/createSeedDataUpload.generated';
import type {
  RetrySeedDataImportMutation,
  RetrySeedDataImportMutationVariables,
} from '../../graphql/queries/retrySeedDataImport.generated';
import type {
  SeedDataErrorReportUrlQuery,
  SeedDataErrorReportUrlQueryVariables,
} from '../../graphql/queries/seedDataErrorReportUrl.generated';
import type {
  SeedDataImportsQuery,
  SeedDataImportsQueryVariables,
} from '../../graphql/queries/seedDataImports.generated';
import {
  CANCEL_SEED_DATA_UPLOAD_DOCUMENT,
  CONFIRM_SEED_DATA_UPLOAD_DOCUMENT,
  CREATE_SEED_DATA_UPLOAD_DOCUMENT,
  RETRY_SEED_DATA_IMPORT_DOCUMENT,
  SEED_DATA_ERROR_REPORT_URL_DOCUMENT,
  SEED_DATA_IMPORTS_DOCUMENT,
} from './seedDataDocuments';

export type SeedDataFetchImpl = Parameters<typeof createAuthenticatedGraphQLClient>[0]['fetchImpl'];

export type SeedDataImport = SeedDataImportsQuery['seedDataImports']['items'][number];
export type SeedDataImportHistory = SeedDataImportsQuery['seedDataImports'];
export type SeedDataUploadReservation = CreateSeedDataUploadMutation['createSeedDataUpload'];
export type SeedDataImportMutationResult = ConfirmSeedDataUploadMutation['confirmSeedDataUpload'];
export type SeedDataErrorReport = SeedDataErrorReportUrlQuery['seedDataErrorReportUrl'];

export type SeedDataApi = {
  createUpload: (input: { originalFilename: string; sizeBytes: number }) => Promise<SeedDataUploadReservation>;
  confirmUpload: (id: string) => Promise<SeedDataImportMutationResult>;
  cancelUpload: (id: string) => Promise<SeedDataImportMutationResult>;
  retryImport: (id: string) => Promise<SeedDataImportMutationResult>;
  listImports: (input: { limit: number; offset: number }, options?: { signal?: AbortSignal }) => Promise<SeedDataImportHistory>;
  getErrorReportUrl: (id: string) => Promise<SeedDataErrorReport>;
};

type AuthenticatedClient = ReturnType<typeof createAuthenticatedGraphQLClient>;
type AuthenticatedClientFactory = (options: Parameters<typeof createAuthenticatedGraphQLClient>[0]) => AuthenticatedClient;

export type CreateSeedDataApiOptions = {
  env?: RuntimeEnvironment;
  fetchImpl?: SeedDataFetchImpl;
  authenticatedClientFactory?: AuthenticatedClientFactory;
};

// Field Ops derives brokerage and broker authorization from the bearer token. The
// browser supplies only the operation inputs defined below; it never sends tenant,
// role, bucket, object-key, or presigned URL values through GraphQL.
export const createSeedDataApi = ({
  env = resolveClientEnv(),
  fetchImpl,
  authenticatedClientFactory = createAuthenticatedGraphQLClient,
}: CreateSeedDataApiOptions = {}): SeedDataApi => {
  const client = authenticatedClientFactory({ env, fetchImpl });

  return {
    createUpload: async input => {
      const variables: CreateSeedDataUploadMutationVariables = { input };
      const result = await client.request<CreateSeedDataUploadMutation>({
        document: CREATE_SEED_DATA_UPLOAD_DOCUMENT,
        variables,
        operationName: 'CreateSeedDataUpload',
      });

      return result.createSeedDataUpload;
    },
    confirmUpload: async id => {
      const variables: ConfirmSeedDataUploadMutationVariables = { id };
      const result = await client.request<ConfirmSeedDataUploadMutation>({
        document: CONFIRM_SEED_DATA_UPLOAD_DOCUMENT,
        variables,
        operationName: 'ConfirmSeedDataUpload',
      });

      return result.confirmSeedDataUpload;
    },
    cancelUpload: async id => {
      const variables: CancelSeedDataUploadMutationVariables = { id };
      const result = await client.request<CancelSeedDataUploadMutation>({
        document: CANCEL_SEED_DATA_UPLOAD_DOCUMENT,
        variables,
        operationName: 'CancelSeedDataUpload',
      });

      return result.cancelSeedDataUpload;
    },
    retryImport: async id => {
      const variables: RetrySeedDataImportMutationVariables = { id };
      const result = await client.request<RetrySeedDataImportMutation>({
        document: RETRY_SEED_DATA_IMPORT_DOCUMENT,
        variables,
        operationName: 'RetrySeedDataImport',
      });

      return result.retrySeedDataImport;
    },
    listImports: async ({ limit, offset }, options = {}) => {
      const variables: SeedDataImportsQueryVariables = { limit, offset };
      const result = await client.request<SeedDataImportsQuery>({
        document: SEED_DATA_IMPORTS_DOCUMENT,
        variables,
        operationName: 'SeedDataImports',
        signal: options.signal,
      });

      return result.seedDataImports;
    },
    getErrorReportUrl: async id => {
      const variables: SeedDataErrorReportUrlQueryVariables = { id };
      const result = await client.request<SeedDataErrorReportUrlQuery>({
        document: SEED_DATA_ERROR_REPORT_URL_DOCUMENT,
        variables,
        operationName: 'SeedDataErrorReportUrl',
      });

      return result.seedDataErrorReportUrl;
    },
  };
};
